import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useContext, useState, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import PropTypes from 'prop-types'

// Internal components
import ArticleList from '../components/ArticleList/ArticleList'
import BannerBlock from '../components/BannerBlock/BannerBlock'
import CalendarPreview from '../components/CalendarPreview/CalendarPreview'
import SymbolBlock from '../components/SymbolBlock/SymbolBlock'
import ChecklistFigure from '../components/ChecklistFigure/ChecklistFigure'
import Correlated from '../components/Correlated/Correlated'
import InstructionHero from '../components/InstructionHero/InstructionHero'
import LayoutHome from '../components/LayoutHome/LayoutHome'
import Slideshow from '../components/Slideshow/Slideshow'

// Utils
import getCalendarSeason from '../utils/auth/getCalendarSeason'
import getChecklistFigureData from '../utils/data/getChecklistFigureData'
import getHighlightedSearchUrl from '../utils/data/getHighlightedSearchUrl'
import getSymbolBlockData from '../utils/data/getSymbolBlockData'
import getSession from '../utils/auth/getSession'
import getSiteHandle from '../utils/i18n/getSiteHandle'
import queryServerSideData from '../utils/data/queryServerSideData'

// Contexts
import { UserContext } from '@/context/UserContext'

const HomePage = (props) => {
  const { entry, calendarPreview, session, siteHandle} = props

  // Contexts
  const userContext = useContext(UserContext)

  // Translations
  const { t } = useTranslation(
    userContext?.userSession?.sportType?.slug ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_NAMESPACE ??
      process.env.NEXT_PUBLIC_RINUS_DEFAULT_SPORT
  )

  // Prepare data for component
  const symbolBlockData = getSymbolBlockData(entry)
  const checklistFiguresData = getChecklistFigureData(entry)

  // States
  const [slideshowExerciseCardsState, setSlideshowExerciseCardsState] = useState([])
  const [slideshowTrainingCardsState, setSlideshowTrainingCardsState] = useState([])

  useEffect(() => {
    if (!userContext.loggedIn) return false
    const ageId = userContext?.userSession?.team?.teamAgeId

    const highlightedTrainingsUrl = getHighlightedSearchUrl(0, 15, 'trainings', ageId, siteHandle)
    setHighlightedSlideshow(highlightedTrainingsUrl, 'trainings')

    const highlightedExercisesUrl = getHighlightedSearchUrl(0, 15, 'exercises', ageId, siteHandle)
    setHighlightedSlideshow(highlightedExercisesUrl, 'exercises')
  }, [userContext?.loggedIn])

  const setHighlightedSlideshow = async (url, type) => {
    if (
      !url ||
      !type ||
      (slideshowExerciseCardsState?.length > 0 && type === 'exercises') ||
      (slideshowTrainingCardsState?.length > 0 && type === 'trainings')
    )
      return false
    const searchResponse = await fetch(`/api/knvb/search?url=${encodeURIComponent(url)}&site=${siteHandle}`)
    const searchResults = await searchResponse.json()
    if (searchResults?.results) {
      if (type === 'exercises') setSlideshowExerciseCardsState([...searchResults.results])
      if (type === 'trainings') setSlideshowTrainingCardsState([...searchResults.results])
    }
  }

  // Show match button when match is ongoing
  const matchOnGoing =
    Object.keys(calendarPreview).length && calendarPreview?.items?.find((o) => o.event?.matchStatus === 'ongoing')

  const calendarButton =
    matchOnGoing?.event?.matchStatus === 'ongoing'
      ? { label: 'CALENDAR_PREVIEW__BTN_SHOWMATCH', link: `/match/id/${matchOnGoing?.event?.id}` }
      : { label: 'CALENDAR_PREVIEW__BTN_FULLCALENDAR', link: '/calendar' }

  return (
    <LayoutHome
      modifiers={[userContext.loggedIn ? 'LayoutHome--loggedIn' : '']}
      headerContent={
        <>
          {userContext.loggedIn ? (
            calendarPreview && (
              <CalendarPreview
                data={calendarPreview}
                team={{ title: userContext?.userSession?.team?.shortTeamName }}
                button={calendarButton}
              />
            )
          ) : (
            <InstructionHero videoUrl={entry?.videoUrl || false} />
          )}
        </>
      }
      mainContent={
        <>
          {userContext.loggedIn ? (
            <>
              {symbolBlockData && (
                <SymbolBlock
                  data={symbolBlockData}
                  matchMode={session?.matchMode}
                  modifiers={['SymbolBlock--quickLinks']}
                />
              )}
              {entry?.articleListHomeLoggedIn?.[0] && (
                <ArticleList {...entry.articleListHomeLoggedIn[0]} modifiers={['ArticleList--homepage']} />
              )}
              {slideshowExerciseCardsState?.length > 0 && (
                <Slideshow title={t('SLIDESHOW__FEATURED_EXERCISES')} cards={slideshowExerciseCardsState} id={0} />
              )}
              {slideshowTrainingCardsState?.length > 0 && (
                <Slideshow title={t('SLIDESHOW__FEATURED_TRAININGS')} cards={slideshowTrainingCardsState} id={1} />
              )}
            </>
          ) : (
            <>
              {checklistFiguresData &&
                checklistFiguresData.map((item, itemIndex) => {
                  return <ChecklistFigure {...item} key={`ChecklistFigure--${itemIndex}`} />
                })}
              {symbolBlockData && <SymbolBlock data={symbolBlockData} />}
              {entry?.bannerBlockHomeLoggedOutTop?.[0] && (
                <BannerBlock {...entry?.bannerBlockHomeLoggedOutTop?.[0]} modifiers={['BannerBlock--homepage']} />
              )}
              {entry?.correlatedHomeLoggedOut?.[0] && (
                <Correlated {...entry.correlatedHomeLoggedOut[0]} onHomepage={true} />
              )}
            </>
          )}
        </>
      }
      footerContent={
        <>
          {userContext.loggedIn
            ? entry?.bannerBlockHomeLoggedIn?.[0] && (
                <BannerBlock {...entry.bannerBlockHomeLoggedIn[0]} modifiers={['BannerBlock--onFooter']} />
              )
            : entry?.bannerBlockHomeLoggedOutBottom?.[0] && (
                <BannerBlock {...entry.bannerBlockHomeLoggedOutBottom[0]} modifiers={['BannerBlock--onFooter']} />
              )}
        </>
      }
    />
  )
}

// Data fetch
export async function getServerSideProps({ req, res, locale }) {
  const session = await getSession(req, res)
  const siteHandle = getSiteHandle(locale)

  if (session.error) {
    return {
      props: {
        error: true,
        errorCode: session.status,
        message: session.message,
        ...(await serverSideTranslations(
          locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE,
          process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(',')
        ))
      }
    }
  }

  const globalJson = await queryServerSideData(
    'qGlobal.graphql',
    { site: siteHandle, seoUri: '/', seoSite: siteHandle },
    session.cookieString
  )

  const homepageJson = await queryServerSideData(
    'qHomepageDetail.graphql',
    { site: siteHandle, siteHandle: siteHandle, loggedin: session?.code !== 401 },
    session.cookieString
  )

  const calendarPreview = session.code !== 401 ? await getCalendarSeason(req, res, true) : {}

  return {
    props: {
      site: siteHandle,
      ...(await serverSideTranslations(
        locale || process.env.NEXT_PUBLIC_RINUS_DEFAULT_LANGUAGE,
        process.env.NEXT_PUBLIC_RINUS_NAMESPACES?.split(',')
      )),
      ...globalJson.data,
      ...homepageJson.data,
      calendarPreview,
      siteHandle,
      // We are using the previewData to get the user login data in the UserContext
      session
    }
  }
}

HomePage.propTypes = {
  site: PropTypes.string,
  pageTitle: PropTypes.string,
  entry: PropTypes.object,
  calendarPreview: PropTypes.object,
  session: PropTypes.object,
  siteHandle: PropTypes.string
}

export default HomePage
